import {
  computed,
  inject,
  Injectable,
  Injector,
  resource,
  ResourceRef,
  runInInjectionContext,
  Signal,
  untracked,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ICountry, IState } from '@okcargo-apps/shared-domain-ionic';
import { HttpService } from '@okcargo-apps/shared-infrastructure';
import { filter, firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CountryStateService {
  private readonly http = inject(HttpService);
  private readonly injector = inject(Injector);

  private readonly countriesResource = resource({
    loader: () =>
      firstValueFrom(this.http.get<ICountry[]>('/assets/country-state/countries.json')).catch((error) => {
        console.error('Error loading countries', error);
        return [] as ICountry[];
      }),
  });

  public readonly countries = computed(() => this.countriesResource.value() ?? []);
  public readonly countriesLoading = this.countriesResource.isLoading;

  private readonly statesResources = new Map<string, ResourceRef<IState[] | undefined>>();

  private getStatesResource(countryIsoCode: string): ResourceRef<IState[] | undefined> {
    let res = this.statesResources.get(countryIsoCode);
    if (!res) {
      res = untracked(() =>
        runInInjectionContext(this.injector, () =>
          resource({
            loader: () =>
              firstValueFrom(this.http.get<IState[]>(`/assets/country-state/${countryIsoCode}.json`)).catch((error) => {
                console.error(`Error loading states for country ${countryIsoCode}`, error);
                return [] as IState[];
              }),
          }),
        ),
      );
      this.statesResources.set(countryIsoCode, res);
    }
    return res;
  }

  public statesForCountryCode(countryIsoCode: Signal<string | undefined>): {
    value: Signal<IState[]>;
    isLoading: Signal<boolean>;
    error: Signal<unknown>;
  } {
    return {
      value: computed(() => {
        const code = countryIsoCode();
        return code ? (this.getStatesResource(code).value() ?? []) : [];
      }),
      isLoading: computed(() => {
        const code = countryIsoCode();
        return code ? this.getStatesResource(code).isLoading() : false;
      }),
      error: computed(() => {
        const code = countryIsoCode();
        return code ? this.getStatesResource(code).error() : undefined;
      }),
    };
  }

  public async findCountryByCode(isoCode: string): Promise<string> {
    const country = this.countries().find((c) => c.isoCode === isoCode);
    return country?.name ?? '';
  }

  public async findStateByCode(countryCode: string, stateCode: string): Promise<IState | undefined> {
    const res = this.getStatesResource(countryCode);
    if (!res.hasValue()) {
      await runInInjectionContext(this.injector, () =>
        firstValueFrom(toObservable(res.value).pipe(filter((v) => v !== undefined))),
      );
    }
    return res.value()?.find((state) => state.stateCode === stateCode);
  }
}
